import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card, Form, Input, Dropdown, Button, Icon, Header, Checkbox, Message
} from 'semantic-ui-react';
import { API, showError, showSuccess } from '../../helpers';

const defaultCategoryOptions = [
  { key: 'llm', text: 'LLM', value: 'llm' },
  { key: 'translation', text: '翻译', value: 'translation' },
  { key: 'asr', text: '语音识别', value: 'asr' },
  { key: 'vision', text: '视觉', value: 'vision' },
  { key: 'embedding', text: 'Embedding', value: 'embedding' },
  { key: 'tts', text: 'TTS', value: 'tts' },
];

// Build GPU options from node info
function buildGpuOptions(node) {
  if (!node || !node.total_gpus) {
    return [{ key: '0', text: 'GPU 0', value: '0' }];
  }
  const opts = [];
  for (let i = 0; i < node.total_gpus; i++) {
    opts.push({ key: String(i), text: `GPU ${i}`, value: String(i) });
  }
  // Parse NVLink groups and add combo options
  let nvlinkGroups = [];
  try {
    if (node.nvlink_groups) {
      nvlinkGroups = JSON.parse(node.nvlink_groups);
    }
  } catch (e) { /* ignore */ }
  if (Array.isArray(nvlinkGroups)) {
    for (const group of nvlinkGroups) {
      if (Array.isArray(group) && group.length > 1) {
        const val = group.join(',');
        opts.push({ key: val, text: `GPU ${group.join('+')} (NVLink)`, value: val });
      }
    }
  }
  return opts;
}

// Build TP options from max GPU count
function buildTpOptions(maxGpus) {
  const opts = [{ key: 1, text: 'TP=1', value: 1 }];
  for (let i = 2; i <= (maxGpus || 2); i++) {
    opts.push({ key: i, text: `TP=${i}`, value: i });
  }
  return opts;
}

const EditModel = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;

  const [loading, setLoading] = useState(false);
  const [manifestParsing, setManifestParsing] = useState(false);
  const [nodes, setNodes] = useState([]);
  const [gpuOptions, setGpuOptions] = useState([{ key: '0', text: 'GPU 0', value: '0' }]);
  const [tpOptions, setTpOptions] = useState([{ key: 1, text: 'TP=1', value: 1 }]);
  const [categoryOptions, setCategoryOptions] = useState(defaultCategoryOptions);
  const [form, setForm] = useState({
    model_name: '',
    display_name: '',
    gpu_indices: '0',
    tensor_parallel: 1,
    expected_memory_mb: 0,
    cuda_overhead_mb: 1800,
    gpu_memory_util: 0,
    max_model_len: 0,
    requires_nvlink: false,
    k8s_namespace: '',
    k8s_deployment: '',
    manifest_path: '',
    enable_tool_choice: false,
    tool_call_parser: '',
    chat_template: '',
    service_port: 0,
    model_category: 'llm',
    is_resident: false,
    channel_id: 0,
    node_id: 1,
  });

  const loadNodes = useCallback(async () => {
    try {
      const res = await API.get('/api/gpu/nodes');
      const { success, data } = res.data;
      if (success && Array.isArray(data) && data.length > 0) {
        setNodes(data);
        const node = data.find(n => n.id === form.node_id) || data[0];
        setGpuOptions(buildGpuOptions(node));
        setTpOptions(buildTpOptions(node.total_gpus));
      }
    } catch (e) { /* ignore, use defaults */ }
  }, []);

  useEffect(() => {
    loadNodes();
    if (isEdit) {
      loadModel();
    }
  }, [id]);

  const loadModel = async () => {
    setLoading(true);
    try {
      const res = await API.get(`/api/model-manager/models/${id}`);
      const { success, data } = res.data;
      if (success && data) {
        setForm({
          model_name: data.model_name || '',
          display_name: data.display_name || '',
          gpu_indices: data.gpu_indices || '0',
          tensor_parallel: data.tensor_parallel || 1,
          expected_memory_mb: data.expected_memory_mb || 0,
          cuda_overhead_mb: data.cuda_overhead_mb || 1800,
          gpu_memory_util: data.gpu_memory_util || 0,
          max_model_len: data.max_model_len || 0,
          requires_nvlink: data.requires_nvlink || false,
          k8s_namespace: data.k8s_namespace || '',
          k8s_deployment: data.k8s_deployment || '',
          manifest_path: data.manifest_path || '',
          enable_tool_choice: data.enable_tool_choice || false,
          tool_call_parser: data.tool_call_parser || '',
          chat_template: data.chat_template || '',
          service_port: data.service_port || 0,
          model_category: data.model_category || 'llm',
          is_resident: data.is_resident || false,
          channel_id: data.channel_id || 0,
          node_id: data.node_id || 1,
        });
      }
    } catch (e) {
      showError(e.message);
    }
    setLoading(false);
  };

  const handleSubmit = async () => {
    if (!form.model_name) {
      showError('模型名称不能为空');
      return;
    }
    setLoading(true);
    try {
      let res;
      if (isEdit) {
        res = await API.put(`/api/model-manager/models/${id}`, form);
      } else {
        res = await API.post('/api/model-manager/models', form);
      }
      const { success, message } = res.data;
      if (success) {
        showSuccess(isEdit ? '模型已更新' : '模型已注册');
        navigate('/model-manager');
      } else {
        showError(message);
      }
    } catch (e) {
      showError(e.message);
    }
    setLoading(false);
  };

  const handleAddCategory = (e, { value }) => {
    setCategoryOptions(prev => [...prev, { key: value, text: value, value }]);
  };

  const loadManifestFields = async (path) => {
    if (!path) return;
    setManifestParsing(true);
    try {
      const res = await API.get(`/api/model-manager/manifest-parse?path=${encodeURIComponent(path)}`);
      const { success, data } = res.data;
      if (success && data) {
        setForm(prev => ({
          ...prev,
          gpu_indices: data.gpu_indices || prev.gpu_indices,
          gpu_memory_util: data.gpu_memory_util || prev.gpu_memory_util,
          max_model_len: data.max_model_len || prev.max_model_len,
          tensor_parallel: data.tensor_parallel || prev.tensor_parallel,
          enable_tool_choice: data.enable_tool_choice,
          tool_call_parser: data.tool_call_parser || prev.tool_call_parser,
          chat_template: data.chat_template || prev.chat_template,
        }));
      }
    } catch (e) {
      /* ignore parse errors silently */
    }
    setManifestParsing(false);
  };

  const updateField = (field, value) => {
    const updated = { ...form, [field]: value };
    if (field === 'gpu_indices') {
      const indices = value.split(',');
      if (indices.length > 1) {
        updated.requires_nvlink = true;
      }
    }
    if (field === 'tensor_parallel' && value > 1) {
      // Auto-select first NVLink group if available
      const nvlinkOpt = gpuOptions.find(o => o.value.includes(','));
      if (nvlinkOpt) {
        updated.gpu_indices = nvlinkOpt.value;
      }
      updated.requires_nvlink = true;
    }
    if (field === 'node_id') {
      const node = nodes.find(n => n.id === value);
      if (node) {
        setGpuOptions(buildGpuOptions(node));
        setTpOptions(buildTpOptions(node.total_gpus));
      }
    }
    setForm(updated);
  };

  const totalVRAM = form.expected_memory_mb + form.cuda_overhead_mb;

  return (
    <div className='dashboard-container'>
      <Card fluid className='chart-card'>
        <Card.Content>
          <Card.Header className='header'>
            <Icon name='cogs' /> {isEdit ? '编辑模型' : '注册新模型'}
          </Card.Header>
        </Card.Content>
        <Card.Content>
          <Form loading={loading}>
            <Header as='h4'>基本信息</Header>
            <Form.Group widths='equal'>
              <Form.Field required>
                <label>模型名称 (唯一标识){isEdit && <span style={{ color: '#e07b39', marginLeft: 8, fontSize: '0.85em' }}>修改后需重新同步所在档位</span>}</label>
                <Input
                  value={form.model_name}
                  onChange={(e, { value }) => updateField('model_name', value)}
                  placeholder='如: llama-70b'
                />
              </Form.Field>
              <Form.Field>
                <label>显示名称</label>
                <Input
                  value={form.display_name}
                  onChange={(e, { value }) => updateField('display_name', value)}
                  placeholder='如: Llama 3.3 70B AWQ'
                />
              </Form.Field>
              <Form.Field>
                <label>模型类别</label>
                <Dropdown
                  selection
                  search
                  allowAdditions
                  additionLabel='新增: '
                  options={categoryOptions}
                  value={form.model_category}
                  onAddItem={handleAddCategory}
                  onChange={(e, { value }) => updateField('model_category', value)}
                />
              </Form.Field>
            </Form.Group>

            <Header as='h4'>GPU 配置</Header>
            <Form.Group widths='equal'>
              <Form.Field>
                <label>目标 GPU</label>
                <Dropdown
                  selection
                  options={gpuOptions}
                  value={form.gpu_indices}
                  onChange={(e, { value }) => updateField('gpu_indices', value)}
                />
              </Form.Field>
              <Form.Field>
                <label>Tensor Parallel</label>
                <Dropdown
                  selection
                  options={tpOptions}
                  value={form.tensor_parallel}
                  onChange={(e, { value }) => updateField('tensor_parallel', value)}
                />
              </Form.Field>
              <Form.Field>
                <label>GPU Memory Utilization</label>
                <Input
                  type='number'
                  step='0.01'
                  value={form.gpu_memory_util}
                  onChange={(e, { value }) => updateField('gpu_memory_util', parseFloat(value) || 0)}
                  placeholder='0.48'
                />
              </Form.Field>
            </Form.Group>
            <Form.Group widths='equal'>
              <Form.Field>
                <label>预期显存 (MB/卡)</label>
                <Input
                  type='number'
                  value={form.expected_memory_mb}
                  onChange={(e, { value }) => updateField('expected_memory_mb', parseInt(value) || 0)}
                />
              </Form.Field>
              <Form.Field>
                <label>CUDA 开销 (MB)</label>
                <Input
                  type='number'
                  value={form.cuda_overhead_mb}
                  onChange={(e, { value }) => updateField('cuda_overhead_mb', parseInt(value) || 0)}
                />
              </Form.Field>
              <Form.Field>
                <label>Max Model Length</label>
                <Input
                  type='number'
                  value={form.max_model_len}
                  onChange={(e, { value }) => updateField('max_model_len', parseInt(value) || 0)}
                />
              </Form.Field>
            </Form.Group>
            {totalVRAM > 0 && (
              <Message info>
                <Message.Header>显存预估</Message.Header>
                <p>总需求: {totalVRAM} MB/卡 ({(totalVRAM / 1024).toFixed(1)} GB/卡)</p>
              </Message>
            )}

            <Header as='h4'>K8s 部署配置</Header>
            <Form.Group widths='equal'>
              <Form.Field>
                <label>Namespace</label>
                <Input
                  value={form.k8s_namespace}
                  onChange={(e, { value }) => updateField('k8s_namespace', value)}
                  placeholder='ai-llm'
                />
              </Form.Field>
              <Form.Field>
                <label>Deployment 名称</label>
                <Input
                  value={form.k8s_deployment}
                  onChange={(e, { value }) => updateField('k8s_deployment', value)}
                  placeholder='vllm-llama70b'
                />
              </Form.Field>
              <Form.Field>
                <label>Manifest 路径 (可选){manifestParsing && <span style={{ color: '#888', marginLeft: 8, fontSize: '0.85em' }}>解析中...</span>}</label>
                <Input
                  value={form.manifest_path}
                  onChange={(e, { value }) => updateField('manifest_path', value)}
                  onBlur={(e) => loadManifestFields(e.target.value)}
                  placeholder='/manifests/model.yaml'
                />
              </Form.Field>
            </Form.Group>

            <Header as='h4'>工具调用配置</Header>
            <Form.Group>
              <Form.Field>
                <Checkbox
                  label='启用工具调用 (--enable-auto-tool-choice)'
                  checked={form.enable_tool_choice}
                  onChange={(e, { checked }) => updateField('enable_tool_choice', checked)}
                />
              </Form.Field>
            </Form.Group>
            {form.enable_tool_choice && (
              <Form.Group widths='equal'>
                <Form.Field required>
                  <label>Tool Call Parser</label>
                  <Dropdown
                    selection
                    options={[
                      { key: 'mistral', text: 'mistral (Mistral/Ministral 系列)', value: 'mistral' },
                      { key: 'llama3_json', text: 'llama3_json (Llama 3.x 系列)', value: 'llama3_json' },
                      { key: 'pythonic', text: 'pythonic (Gemma 3 / 通用)', value: 'pythonic' },
                      { key: 'gemma4', text: 'gemma4 (Gemma 4 系列)', value: 'gemma4' },
                      { key: 'hermes', text: 'hermes (Hermes/Qwen 系列)', value: 'hermes' },
                      { key: 'qwen3_coder', text: 'qwen3_coder (Qwen3 系列)', value: 'qwen3_coder' },
                      { key: 'internlm', text: 'internlm (InternLM 系列)', value: 'internlm' },
                      { key: 'openai', text: 'openai (OpenAI OSS 系列)', value: 'openai' },
                    ]}
                    value={form.tool_call_parser}
                    onChange={(e, { value }) => updateField('tool_call_parser', value)}
                    placeholder='选择解析器'
                  />
                </Form.Field>
                <Form.Field />
                <Form.Field />
              </Form.Group>
            )}
            <Form.Group widths='equal'>
              <Form.Field>
                <label>自定义 Chat Template 路径 (可选)</label>
                <Input
                  value={form.chat_template}
                  onChange={(e, { value }) => updateField('chat_template', value)}
                  placeholder='/vllm-workspace/examples/tool_chat_template_gemma3_pythonic.jinja'
                />
              </Form.Field>
              <Form.Field />
              <Form.Field />
            </Form.Group>

            <Header as='h4'>关联配置</Header>
            <Form.Group widths='equal'>
              <Form.Field>
                <label>关联渠道 ID</label>
                <Input
                  type='number'
                  value={form.channel_id}
                  onChange={(e, { value }) => updateField('channel_id', parseInt(value) || 0)}
                />
              </Form.Field>
              <Form.Field>
                <label>服务端口</label>
                <Input
                  type='number'
                  value={form.service_port}
                  onChange={(e, { value }) => updateField('service_port', parseInt(value) || 0)}
                  placeholder='8001'
                />
              </Form.Field>
              <Form.Field>
                <label>GPU 节点</label>
                <Dropdown
                  selection
                  options={nodes.length > 0
                    ? nodes.map(n => ({
                      key: n.id,
                      text: `${n.node_name} (${n.total_gpus}x ${n.gpu_model})`,
                      value: n.id,
                    }))
                    : [{ key: 1, text: '默认节点', value: 1 }]
                  }
                  value={form.node_id}
                  onChange={(e, { value }) => updateField('node_id', value)}
                />
              </Form.Field>
            </Form.Group>
            <Form.Group>
              <Form.Field>
                <Checkbox
                  label='常驻模型'
                  checked={form.is_resident}
                  onChange={(e, { checked }) => updateField('is_resident', checked)}
                />
              </Form.Field>
              <Form.Field style={{ marginLeft: 24 }}>
                <Checkbox
                  label='需要 NVLink'
                  checked={form.requires_nvlink}
                  onChange={(e, { checked }) => updateField('requires_nvlink', checked)}
                />
              </Form.Field>
            </Form.Group>

            <div style={{ marginTop: 16 }}>
              <Button primary onClick={handleSubmit} loading={loading}>
                <Icon name='save' /> {isEdit ? '保存修改' : '注册模型'}
              </Button>
              <Button onClick={() => navigate('/model-manager')}>取消</Button>
            </div>
          </Form>
        </Card.Content>
      </Card>
    </div>
  );
};

export default EditModel;
